/**
 * Page Browse publique — Toutes les décisions
 * Route : /jurisprudence/browse
 *
 * Pagination par cursor (next_cursor) au lieu d'offset numérique,
 * car Qdrant order_by ne retourne pas next_page_offset quand plusieurs
 * points partagent la même date (order_value identique).
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { getDateLocale } from '@/i18n';
import { Loader } from '@/components/Loader';
import {
  PublicHeader, PublicFooter, FicheCard, MCPBadge, Breadcrumb, 
  AdvancedSearchPanel, ActiveFilterBadge, EMPTY_ADV_FILTERS,
  type PublicFiche, type AdvancedFilters, 
} from '@/components/jurisprudence';

const LIMIT    = 20;
const SITE_URL = 'https://smartlawyer.ai';

interface BrowseResult {
  total:       number;
  limit:       number;
  next_cursor: string | null;
  results:     PublicFiche[];
}

interface Filters extends AdvancedFilters { query: string }
const EMPTY_FILTERS: Filters = { query: '', ...EMPTY_ADV_FILTERS };

export default function PublicBrowse() {
  const { t } = useTranslation();
  const locale = getDateLocale();
  const [searchParams, setSearchParams] = useSearchParams();

  const filtersFromUrl = (): Filters => ({
    query:         searchParams.get('q')             || '',
    jurisdiction:  searchParams.get('jurisdiction')  || '',
    chamber:       searchParams.get('chamber')       || '',
    domaine:       searchParams.get('domaine')       || '',
    solution:      searchParams.get('solution')      || '',
    date_from:     searchParams.get('date_from')     || '',
    date_to:       searchParams.get('date_to')       || '',
    quality_min:   searchParams.get('quality_min')   || '',
    enriched_only: searchParams.get('enriched_only') === 'true',
  });

  const [filters, setFilters]           = useState<Filters>(filtersFromUrl);
  const [pending, setPending]           = useState<Filters>(filtersFromUrl);
  // Replié si on arrive avec des filtres URL pré-remplis (ex: clic sur un domaine)
  const [showAdvanced, setShowAdvanced] = useState(() => {
    const hasUrlFilters = ['q','domaine','jurisdiction','chamber','solution','date_from','date_to','quality_min','enriched_only']
      .some(k => searchParams.get(k));
    return !hasUrlFilters;
  });
  const [loading, setLoading]           = useState(true);
  const [initialized, setInitialized]   = useState(false);
  const [data, setData]                 = useState<BrowseResult | null>(null);
  const [page, setPage]                 = useState(0);
  const [searchTime, setSearchTime]     = useState<number | null>(null);
  // Marqueur d'erreur — le message est traduit au rendu
  const [error, setError]               = useState<string | null>(null);

  // Historique des curseurs indexé par numéro de page.
  // cursors[0] = null  (page 0, pas de cursor à passer)
  // cursors[1] = next_cursor retourné par page 0
  // cursors[2] = next_cursor retourné par page 1, etc.
  const cursorsRef = useRef<(string | null)[]>([null]);

  const hasActiveFilters = Object.entries(filters).some(([k, v]) =>
    k !== 'query' && (v === true || (typeof v === 'string' && v !== ''))
  );

  const buildParams = (f: Filters, cursor: string | null): URLSearchParams => {
    const params = new URLSearchParams();
    params.set('limit', String(LIMIT));
    if (cursor)          params.set('cursor',       cursor);
    if (f.query)         params.set('query',        f.query);
    if (f.jurisdiction)  params.set('jurisdiction', f.jurisdiction);
    if (f.chamber)       params.set('chamber',      f.chamber);
    if (f.domaine)       params.set('domaine',      f.domaine);
    if (f.solution)      params.set('solution',     f.solution);
    if (f.date_from)     params.set('date_from',    f.date_from);
    if (f.date_to)       params.set('date_to',      f.date_to);
    if (f.quality_min)   params.set('quality_min',  f.quality_min);
    if (f.enriched_only) params.set('enriched_only','true');
    return params;
  };
  
  const fetchPage = useCallback(async (
    f: Filters,
    pageNum: number,
    cursorHistory: (string | null)[],
  ) => {
    setLoading(true);
    setSearchTime(null);
    setError(null);
    const t0     = performance.now();
    const cursor = pageNum === 0 ? null : (cursorHistory[pageNum] ?? null);
  
    try {
      const res = await fetch(`/api/fiches/browse-sorted?${buildParams(f, cursor)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json: BrowseResult = await res.json();
      setData(json);
      setSearchTime(Math.round(performance.now() - t0));
  
      // Stocker le cursor retourné pour la page suivante
      if (json.next_cursor != null) {
        cursorHistory[pageNum + 1] = json.next_cursor;
      }
    } catch (e) {
      console.error('browse-sorted error:', e);
      setError('load');
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, []);

  // Init
  useEffect(() => {
    const f = filtersFromUrl();
    setFilters(f);
    setPending(f);
    cursorsRef.current = [null];
    fetchPage(f, 0, cursorsRef.current);
  }, []);

  const resetCursors = () => { cursorsRef.current = [null]; };

  const applyFilters = () => {
    setFilters(pending);
    setPage(0);
    resetCursors();
    const params: Record<string, string> = {};
    if (pending.query)         params.q             = pending.query;
    if (pending.jurisdiction)  params.jurisdiction  = pending.jurisdiction;
    if (pending.chamber)       params.chamber       = pending.chamber;
    if (pending.domaine)       params.domaine       = pending.domaine;
    if (pending.solution)      params.solution      = pending.solution;
    if (pending.date_from)     params.date_from     = pending.date_from;
    if (pending.date_to)       params.date_to       = pending.date_to;
    if (pending.quality_min)   params.quality_min   = pending.quality_min;
    if (pending.enriched_only) params.enriched_only = 'true';
    setSearchParams(params);
    fetchPage(pending, 0, cursorsRef.current);
  };

  const resetFilters = () => {
    setPending(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setPage(0);
    resetCursors();
    setSearchParams({});
    fetchPage(EMPTY_FILTERS, 0, cursorsRef.current);
  };

  const goToPage = (p: number) => {
    if (p > 0 && cursorsRef.current[p] === undefined) return;
    setPage(p);
    fetchPage(filters, p, cursorsRef.current);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearFilter = (key: keyof Filters, value: string | boolean) => {
    const next = { ...filters, [key]: value } as Filters;
    setFilters(next);
    setPending(next);
    setPage(0);
    resetCursors();
    fetchPage(next, 0, cursorsRef.current);
  };

  const totalPages    = data ? Math.ceil((data.total ?? 0) / LIMIT) : 0;
  const hasNextCursor = data?.next_cursor != null;

  const badges: { label: string; clear: () => void }[] = [
    ...(filters.jurisdiction  ? [{ label: filters.jurisdiction,               clear: () => clearFilter('jurisdiction',  '') }] : []),
    ...(filters.chamber       ? [{ label: filters.chamber,                    clear: () => clearFilter('chamber',       '') }] : []),
    ...(filters.domaine       ? [{ label: filters.domaine,                    clear: () => clearFilter('domaine',       '') }] : []),
    ...(filters.solution      ? [{ label: filters.solution,                   clear: () => clearFilter('solution',      '') }] : []),
    ...(filters.date_from     ? [{ label: t('jurisprudence.browse.badge_from', { date: filters.date_from }), clear: () => clearFilter('date_from',     '') }] : []),
    ...(filters.date_to       ? [{ label: t('jurisprudence.browse.badge_to', { date: filters.date_to }), clear: () => clearFilter('date_to',       '') }] : []),
    ...(filters.quality_min   ? [{ label: t('jurisprudence.browse.badge_quality', { value: filters.quality_min }), clear: () => clearFilter('quality_min',   '') }] : []),
    ...(filters.enriched_only ? [{ label: 'Legal Graph',                      clear: () => clearFilter('enriched_only', false) }] : []),
  ];

  const renderPagination = () => {
    if (!data) return null;
  
    const results     = data.results ?? [];
    const hasNext     = data.next_cursor != null;
    const hasPrev     = page > 0;
    const moreThanOne = totalPages > 1 || hasNext || hasPrev;
  
    if (!moreThanOne && results.length === 0) return null;
    if (!moreThanOne) return null;
  
    const maxKnown = cursorsRef.current.length - 1;
  
    const pageSet = new Set<number>([0, page]);
    if (hasNext) pageSet.add(page + 1);
    for (let p = Math.max(0, page - 1); p <= Math.min(maxKnown, page + 1); p++) pageSet.add(p);
    const pages = Array.from(pageSet).sort((a, b) => a - b);
  
    return (
      <div className="flex items-center justify-center gap-2 mt-10">
        <button
          disabled={page === 0 || loading}
          onClick={() => goToPage(page - 1)}
          className="px-4 py-2 text-sm rounded-lg border hover:bg-accent disabled:opacity-40 transition-colors"
        >
          ← {t('common.previous')}
        </button>
  
        <div className="flex items-center gap-1">
          {pages.map((p, i) => {
            const prev         = pages[i - 1];
            const showEllipsis = prev !== undefined && p - prev > 1;
            const accessible   = p === 0 || cursorsRef.current[p] !== undefined;
            return (
              <div key={p} className="flex items-center gap-1">
                {showEllipsis && <span className="text-muted-foreground px-1">…</span>}
                <button
                  onClick={() => accessible ? goToPage(p) : undefined}
                  disabled={loading || !accessible}
                  className={`w-9 h-9 text-sm rounded-lg transition-colors ${
                    p === page
                      ? 'bg-primary text-primary-foreground font-bold'
                      : accessible
                        ? 'hover:bg-accent border'
                        : 'border opacity-30 cursor-not-allowed'
                  }`}
                >
                  {p + 1}
                </button>
              </div>
            );
          })}
        </div>
  
        <button
          disabled={!hasNext || loading}
          onClick={() => goToPage(page + 1)}
          className="px-4 py-2 text-sm rounded-lg border hover:bg-accent disabled:opacity-40 transition-colors"
        >
          {t('common.next')} →
        </button>
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>{t('jurisprudence.browse.meta_title')}</title>
        <meta name="description" content={t('jurisprudence.browse.meta_description')} />
        <link rel="canonical" href={`${SITE_URL}/jurisprudence/browse`} />
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        <PublicHeader />
        <Breadcrumb items={[
          { label: t('jurisprudence.browse.breadcrumb_home'), to: '/' },
          { label: t('jurisprudence.browse.title') },
        ]} />
        <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-10">

          <div className="mb-8">
            <h1 className="text-2xl font-bold mb-1">{t('jurisprudence.browse.title')}</h1>
            <p className="text-sm text-muted-foreground">
              {data ? (
                <>
                  <Trans
                    i18nKey="jurisprudence.browse.decisions_count"
                    count={data.total ?? 0}
                    values={{ total: (data.total ?? 0).toLocaleString(locale) }}
                    components={[<strong key="0" className="text-foreground" />]}
                  />
                  {searchTime != null && (
                    <span className="ml-2 text-muted-foreground/60">
                      ({searchTime < 1000 ? `${searchTime} ms` : `${(searchTime / 1000).toFixed(2)} s`})
                    </span>
                  )}
                  {page > 0 && <span className="ml-2 text-muted-foreground/60">{t('jurisprudence.browse.page_suffix', { page: page + 1 })}</span>}
                </>
              ) : t('common.loading')}
              {' '}{t('jurisprudence.browse.sorted_desc')}
            </p>
          </div>

          {/* Barre de recherche + filtres */}
          <div className="mb-8">
            <div className="flex gap-2">
              <div className="flex-1 flex items-center gap-3 rounded-xl border-2 border-border bg-card shadow-sm px-4 py-2.5 focus-within:border-primary transition-colors">
                <svg className="h-4 w-4 text-muted-foreground shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={pending.query}
                  onChange={e => setPending(f => ({ ...f, query: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && applyFilters()}
                  placeholder={t('jurisprudence.search_placeholder')}
                  className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
                />
                {pending.query && (
                  <button onClick={() => setPending(f => ({ ...f, query: '' }))}
                    className="text-muted-foreground hover:text-foreground transition-colors">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                )}
              </div>
              <button onClick={applyFilters} disabled={loading}
                className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors shrink-0">
                {loading
                  ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  : t('common.search')}
              </button>
            </div>

            <div className="flex items-center justify-between mt-2 px-1">
              <button
                onClick={() => setShowAdvanced(v => !v)}
                className="text-xs text-muted-foreground hover:text-primary transition-colors flex items-center gap-1"
              >
                <svg className={`w-3 h-3 transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
                {t('jurisprudence.filters')}
              </button>
              {hasActiveFilters && (
                <button onClick={resetFilters}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors">
                  {t('search.clear_filters')}
                </button>
              )}
            </div>

            {showAdvanced && (
              <AdvancedSearchPanel
                filters={pending}
                onChange={f => setPending({ ...pending, ...f })}
                onApply={() => { applyFilters(); setShowAdvanced(false); }}
                onReset={() => setPending(EMPTY_FILTERS)}
              />
            )}

            {badges.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {badges.map(({ label, clear }) => (
                  <ActiveFilterBadge key={label} label={label} onRemove={clear} />
                ))}
              </div>
            )}
          </div>

          {/* Résultats */}
          {!initialized || loading ? (
            <div className="flex flex-col items-center justify-center py-24 gap-4">
              <Loader className="!size-8" />
              <p className="text-sm text-muted-foreground">{t('jurisprudence.browse.loading')}</p>
            </div>
          ) : error ? (
            <div className="text-center py-24">
              <p className="text-5xl mb-4">⚠️</p>
              <p className="font-semibold text-foreground mb-2">{t('jurisprudence.browse.error_loading')}</p>
              <button onClick={() => fetchPage(filters, page, cursorsRef.current)}
                className="mt-4 text-sm text-primary underline hover:no-underline">
                {t('common.retry')}
              </button>
            </div>
          ) : (data?.results ?? []).length === 0 ? (
            <div className="text-center py-24 text-muted-foreground">
              <p className="text-5xl mb-4">🔎</p>
              <p className="font-semibold text-foreground mb-1">{t('jurisprudence.browse.empty_title')}</p>
              <p className="text-sm">{t('jurisprudence.browse.empty_hint')}</p>
              <button onClick={resetFilters} className="mt-4 text-sm text-primary underline hover:no-underline">
                {t('jurisprudence.browse.clear_all_filters')}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {(data?.results ?? []).map(f => <FicheCard key={f.id} fiche={f} />)}
            </div>
          )}

          {renderPagination()}

          {data && (page > 0 || hasNextCursor) && (
            <p className="text-center text-xs text-muted-foreground mt-4">
              {t('jurisprudence.browse.page_n', { page: page + 1 })}
              {totalPages > 1 && t('jurisprudence.browse.page_of', { total: totalPages.toLocaleString(locale) })}
              {' '}· {t('jurisprudence.browse.total_decisions', { count: data.total ?? 0, total: (data.total ?? 0).toLocaleString(locale) })}
            </p>
          )}
        </main>

        <PublicFooter />
      </div>

      <MCPBadge />
    </>
  );
}